import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { Link2, Mail, Check, X, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

function readQuery() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return {
    salePrice: Number(p.get("sale")) || null,
    transactions: Number(p.get("tx")) || null,
    commissionPct: Number(p.get("comm")) || null,
    royaltyPct: Number(p.get("roy")) || null,
    techFee: Number(p.get("tech")) || null,
    eOFee: Number(p.get("eno")) || null,
    tcFee: Number(p.get("tc")) || null,
  };
}

export default function CommissionCalculator() {
  const q = readQuery();
  const [salePrice, setSalePrice] = useState(q.salePrice || 500000);
  const [transactions, setTransactions] = useState(q.transactions || 12);
  const [commissionPct, setCommissionPct] = useState(q.commissionPct || 2.5);
  const [royaltyPct, setRoyaltyPct] = useState(q.royaltyPct || 5);
  const [techFee, setTechFee] = useState(q.techFee || 50);
  const [eOFee, setEOFee] = useState(q.eOFee || 150);
  const [tcFee, setTcFee] = useState(q.tcFee || 500);
  const [firstChoiceTc, setFirstChoiceTc] = useState(350);
  const [useFirstChoiceTc, setUseFirstChoiceTc] = useState(false);

  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const data = useMemo(() => {
    // Agent's gross annual commission (same at both brokerages)
    const grossPerUnit = salePrice * (commissionPct / 100);
    const grossAnnual = grossPerUnit * transactions;

    // Regular brokerage fees
    const royaltyAnnual = grossAnnual * (royaltyPct / 100);
    const techAnnual = techFee * 12;
    const eOAnnual = eOFee * transactions;
    const tcAnnual = tcFee * transactions;
    const regularFeesAnnual = royaltyAnnual + techAnnual + eOAnnual + tcAnnual;
    const regularNet = grossAnnual - regularFeesAnnual;

    // Sold By 1st Choice fees — flat $3,500 + $150 per unit (+ optional TC)
    const firstChoiceFlat = 3500;
    const firstChoicePerUnit = 150;
    const firstChoiceTcAnnual = useFirstChoiceTc ? firstChoiceTc * transactions : 0;
    const firstChoiceFeesAnnual =
      firstChoiceFlat + firstChoicePerUnit * transactions + firstChoiceTcAnnual;
    const firstChoiceNet = grossAnnual - firstChoiceFeesAnnual;

    const diff = firstChoiceNet - regularNet;

    return {
      grossPerUnit,
      grossAnnual,
      royaltyAnnual,
      techAnnual,
      eOAnnual,
      tcAnnual,
      regularFeesAnnual,
      regularNet,
      firstChoiceFeesAnnual,
      firstChoiceNet,
      firstChoiceTcAnnual,
      diff,
      bars: [
        { name: "Regular brokerage", value: Math.max(regularNet, 0), fill: "#a3a3a3" },
        { name: "Sold By 1st Choice", value: Math.max(firstChoiceNet, 0), fill: "#121275" },
      ],
      feeStack: [
        { label: "Royalty", regular: royaltyAnnual, firstChoice: 0 },
        { label: "Tech / mo", regular: techAnnual, firstChoice: 0 },
        { label: "E&O / unit", regular: eOAnnual, firstChoice: 0 },
        { label: "TC / unit", regular: tcAnnual, firstChoice: 0 },
        { label: "Flat / year", regular: 0, firstChoice: firstChoiceFlat },
        { label: "$150 / unit", regular: 0, firstChoice: firstChoicePerUnit * transactions },
        ...(useFirstChoiceTc
          ? [{ label: "Optional TC ($350/unit)", regular: 0, firstChoice: firstChoiceTcAnnual }]
          : []),
      ],
    };
  }, [salePrice, transactions, commissionPct, royaltyPct, techFee, eOFee, tcFee, firstChoiceTc, useFirstChoiceTc]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    p.set("sale", String(salePrice));
    p.set("tx", String(transactions));
    p.set("comm", String(commissionPct));
    p.set("roy", String(royaltyPct));
    p.set("tech", String(techFee));
    p.set("eno", String(eOFee));
    p.set("tc", String(tcFee));
    const newUrl = `${window.location.pathname}?${p.toString()}${window.location.hash || "#calculator"}`;
    window.history.replaceState(null, "", newUrl);
  }, [salePrice, transactions, commissionPct, royaltyPct, techFee, eOFee, tcFee]);

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const base = `${window.location.origin}/`;
    const p = new URLSearchParams({
      sale: String(salePrice),
      tx: String(transactions),
      comm: String(commissionPct),
      roy: String(royaltyPct),
      tech: String(techFee),
      eno: String(eOFee),
      tc: String(tcFee),
    });
    return `${base}?${p.toString()}#calculator`;
  }, [salePrice, transactions, commissionPct, royaltyPct, techFee, eOFee, tcFee]);

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied.");
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast.error("Copy failed. Select the URL manually.");
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/calculator/share", {
        email,
        name,
        gci: Math.round(data.grossAnnual),
        transactions,
        current_split: commissionPct * 100,
        current_fees: Math.round(data.regularFeesAnnual / 12),
        first_choice_net: Math.round(data.firstChoiceNet),
        current_net: Math.round(data.regularNet),
        diff: Math.round(data.diff),
        share_url: shareUrl,
      });
      setSent(true);
      toast.success("PDF on the way. Check your inbox.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const closeEmail = () => {
    setEmailOpen(false);
    setTimeout(() => {
      setSent(false);
      setEmail("");
      setName("");
    }, 250);
  };

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="relative py-24 sm:py-32 lg:py-40 border-t border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-16"
        >
          <div className="overline mb-4">Run the numbers</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#0a0a0a]">
            What are you <em className="not-italic text-[#121275]">leaving on the table</em>?
          </h2>
          <p className="mt-6 text-black/60 max-w-2xl">
            Drag the bars. Add your average sale price, number of homes sold, and what your
            current brokerage charges. See what changes when you move to Sold By 1st Choice.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Inputs */}
          <div className="lg:col-span-5">
            {/* Your production */}
            <div className="mb-10">
              <div className="overline mb-6">Your production</div>
              <div className="space-y-8">
                <SliderRow
                  label="Average sale price"
                  suffix="/home"
                  value={salePrice}
                  onChange={setSalePrice}
                  display={fmt(salePrice)}
                  min={100000}
                  max={3000000}
                  step={10000}
                  gold
                  testid="calc-sale-slider"
                />
                <SliderRow
                  label="Homes sold / year"
                  suffix="units"
                  value={transactions}
                  onChange={setTransactions}
                  display={String(transactions)}
                  min={1}
                  max={100}
                  step={1}
                  testid="calc-tx-slider"
                />
                <SliderRow
                  label="Your commission per home"
                  suffix="of sale"
                  value={commissionPct}
                  onChange={setCommissionPct}
                  display={`${commissionPct.toFixed(1)}%`}
                  min={1}
                  max={4}
                  step={0.1}
                  testid="calc-comm-slider"
                  hint="Typical agent take: 2.5% of sale price"
                />
              </div>
            </div>

            {/* Regular brokerage fees */}
            <div className="border-t border-black/10 pt-10">
              <div className="overline mb-6">Regular brokerage fees</div>
              <div className="space-y-8">
                <SliderRow
                  label="Franchise royalty (% of commission)"
                  suffix="royalty"
                  value={royaltyPct}
                  onChange={setRoyaltyPct}
                  display={`${royaltyPct.toFixed(0)}%`}
                  min={5}
                  max={8}
                  step={0.5}
                  testid="calc-royalty-slider"
                  hint="Typical franchise royalty: 5–8% of gross commission"
                />
                <SliderRow
                  label="Technology fee"
                  suffix="/month"
                  value={techFee}
                  onChange={setTechFee}
                  display={fmt(techFee)}
                  min={0}
                  max={200}
                  step={5}
                  testid="calc-tech-slider"
                />
                <SliderRow
                  label="E&O insurance"
                  suffix="/unit sold"
                  value={eOFee}
                  onChange={setEOFee}
                  display={fmt(eOFee)}
                  min={0}
                  max={400}
                  step={10}
                  testid="calc-eno-slider"
                />
                <SliderRow
                  label="Transaction coordinator"
                  suffix="/unit sold"
                  value={tcFee}
                  onChange={setTcFee}
                  display={fmt(tcFee)}
                  min={0}
                  max={1000}
                  step={25}
                  testid="calc-tc-slider"
                />
              </div>
            </div>

            {/* Sold By 1st Choice optional TC */}
            <div className="border-t border-[#121275]/30 pt-10 mt-10">
              <div className="overline mb-6">Sold By 1st Choice — Optional TC</div>
              <label className="flex items-start gap-3 cursor-pointer mb-6" data-testid="calc-firstchoice-tc-toggle-label">
                <input
                  type="checkbox"
                  checked={useFirstChoiceTc}
                  onChange={(e) => setUseFirstChoiceTc(e.target.checked)}
                  className="mt-1 accent-[#121275] w-4 h-4"
                  data-testid="calc-firstchoice-tc-toggle"
                />
                <div>
                  <div className="text-sm text-[#0a0a0a]">Use our in-house TC</div>
                  <div className="numeric text-[11px] text-black/50 tracking-widest uppercase mt-1">
                    Audit + compliance included · optional
                  </div>
                </div>
              </label>
              <div className={useFirstChoiceTc ? "" : "opacity-40 pointer-events-none"}>
                <SliderRow
                  label="1st Choice TC fee"
                  suffix="/unit sold"
                  value={firstChoiceTc}
                  onChange={setFirstChoiceTc}
                  display={fmt(firstChoiceTc)}
                  min={0}
                  max={800}
                  step={25}
                  testid="calc-firstchoice-tc-slider"
                  gold
                />
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-[#f7f7f7] border border-black/10 p-8 lg:p-10 tracing-border">
              <div className="overline mb-3">Your projected take-home</div>
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                  <div className="text-xs text-black/40 uppercase tracking-widest mb-1 font-mono">
                    Difference / year
                  </div>
                  <div
                    data-testid="calc-difference"
                    className={`numeric text-5xl lg:text-6xl tracking-tight ${
                      data.diff >= 0 ? "text-[#121275]" : "text-red-400"
                    }`}
                    style={{ textShadow: "0 0 28px rgba(212,175,55,0.25)" }}
                  >
                    {data.diff >= 0 ? "+" : ""}
                    {fmt(data.diff)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-black/40 uppercase tracking-widest mb-1 font-mono">
                    10-year impact
                  </div>
                  <div className="numeric text-2xl text-[#0a0a0a]">{fmt(data.diff * 10)}</div>
                </div>
              </div>

              <div className="h-px bg-black/10 my-8" />

              <div className="h-64 -mx-2">
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <BarChart data={data.bars} layout="vertical" margin={{ left: 0, right: 30 }}>
                    <CartesianGrid stroke="rgba(0,0,0,0.08)" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 11, fontFamily: "JetBrains Mono" }}
                      tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                      axisLine={{ stroke: "rgba(0,0,0,0.15)" }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={170}
                      tick={{ fill: "rgba(0,0,0,0.75)", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(212,175,55,0.05)" }}
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid rgba(18,18,117,0.4)",
                        color: "#0a0a0a",
                        fontFamily: "JetBrains Mono",
                        fontSize: 12,
                      }}
                      formatter={(v) => fmt(v)}
                    />
                    <Bar dataKey="value" radius={0}>
                      {data.bars.map((b, i) => (
                        <Cell key={i} fill={b.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-black/10">
                <div>
                  <div className="overline">Gross earnings</div>
                  <div className="numeric text-xl text-[#0a0a0a] mt-1" data-testid="calc-gross">
                    {fmt(data.grossAnnual)}
                  </div>
                  <div className="numeric text-[10px] text-black/40 uppercase tracking-widest mt-1">
                    {fmt(data.grossPerUnit)} × {transactions} units
                  </div>
                </div>
                <div>
                  <div className="overline">1st Choice net</div>
                  <div className="numeric text-xl text-[#121275] mt-1" data-testid="calc-firstchoice-net">
                    {fmt(data.firstChoiceNet)}
                  </div>
                  <div className="numeric text-[10px] text-black/40 uppercase tracking-widest mt-1">
                    − {fmt(data.firstChoiceFeesAnnual)} in fees
                  </div>
                </div>
                <div>
                  <div className="overline !text-black/30">Regular net</div>
                  <div className="numeric text-xl text-black/60 mt-1" data-testid="calc-current-net">
                    {fmt(data.regularNet)}
                  </div>
                  <div className="numeric text-[10px] text-black/40 uppercase tracking-widest mt-1">
                    − {fmt(data.regularFeesAnnual)} in fees
                  </div>
                </div>
              </div>

              {/* Lead-magnet actions */}
              <div className="mt-8 pt-8 border-t border-black/10 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={copyShare}
                  data-testid="calc-copy-link"
                  className="btn-secondary !py-3 group"
                >
                  {copied ? <Check size={16} className="text-[#121275]" /> : <Link2 size={16} />}
                  <span>{copied ? "Copied" : "Copy share link"}</span>
                </button>
                <button
                  onClick={() => setEmailOpen(true)}
                  data-testid="calc-email-pdf"
                  className="btn-primary !py-3 group"
                >
                  <Mail size={16} />
                  Email me the PDF
                </button>
              </div>
            </div>

            {/* Fee breakdown */}
            <div className="bg-[#f7f7f7] border border-black/10 p-8" data-testid="calc-breakdown">
              <div className="flex items-baseline justify-between mb-6">
                <div className="overline">Fee breakdown</div>
                <div className="numeric text-[10px] uppercase tracking-widest text-[#121275]">
                  1st Choice: just 2 line items
                </div>
              </div>
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-black/10 text-[10px] uppercase tracking-widest text-black/40 font-mono">
                    <th className="py-2 pr-4">Fee</th>
                    <th className="py-2 pr-4 text-right">Regular</th>
                    <th className="py-2 pr-4 text-right">1st Choice</th>
                  </tr>
                </thead>
                <tbody>
                  {data.feeStack.map((row) => (
                    <tr key={row.label} className="border-b border-black/5">
                      <td className="py-3 pr-4 text-sm text-black/80">{row.label}</td>
                      <td className="py-3 pr-4 text-right numeric text-sm text-black/70">
                        {row.regular > 0 ? fmt(row.regular) : "—"}
                      </td>
                      <td className="py-3 pr-4 text-right numeric text-sm text-[#121275]">
                        {row.firstChoice > 0 ? fmt(row.firstChoice) : "—"}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="py-3 pr-4 text-sm text-black/60 uppercase tracking-widest text-[10px] font-mono">
                      Total annual fees
                    </td>
                    <td className="py-3 pr-4 text-right numeric text-[#0a0a0a]">
                      {fmt(data.regularFeesAnnual)}
                    </td>
                    <td className="py-3 pr-4 text-right numeric text-[#121275]">
                      {fmt(data.firstChoiceFeesAnnual)}
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-4 text-xs text-black/40 flex items-start gap-2">
                <Info size={12} className="mt-0.5 flex-shrink-0" />
                Excludes E&O and taxes. Sold By 1st Choice charges only $3,500/year flat and
                $150 per unit sold — nothing else.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Email modal */}
      {emailOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          data-testid="calc-email-modal"
          onClick={closeEmail}
        >
          <div
            className="relative w-full max-w-md bg-[#f7f7f7] border border-black/15 p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeEmail}
              aria-label="Close"
              className="absolute top-4 right-4 text-black/60 hover:text-[#0a0a0a]"
            >
              <X size={20} />
            </button>
            {!sent ? (
              <form onSubmit={submitEmail}>
                <div className="overline">Get the PDF</div>
                <h3 className="font-display text-2xl font-light text-[#0a0a0a] mt-2">
                  Send me <em className="not-italic text-[#121275]">my numbers</em>.
                </h3>
                <p className="text-black/60 mt-3 text-sm">
                  We&apos;ll email a one-page PDF of your scenario plus a link to schedule a
                  private walk-through with a senior recruiter.
                </p>

                <div className="mt-8 space-y-6">
                  <div>
                    <label className="field-label">Name (optional)</label>
                    <input
                      className="input-line"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="calc-email-name"
                    />
                  </div>
                  <div>
                    <label className="field-label">Email</label>
                    <input
                      required
                      type="email"
                      className="input-line"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="calc-email-input"
                    />
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-black/10 numeric text-xs text-black/40 tracking-widest uppercase">
                  Diff <span className="text-[#121275]">{fmt(data.diff)}</span> · Regular
                  net {fmt(data.regularNet)} · 1st Choice net {fmt(data.firstChoiceNet)}
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="calc-email-submit"
                  className="btn-primary w-full mt-8 disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send my PDF"}
                </button>
              </form>
            ) : (
              <div className="text-center py-4">
                <div className="mx-auto w-12 h-12 border border-[#121275] flex items-center justify-center">
                  <Check className="text-[#121275]" size={24} />
                </div>
                <div className="overline mt-6">On the way</div>
                <h3 className="font-display text-2xl font-light text-[#0a0a0a] mt-2">
                  Check your inbox.
                </h3>
                <p className="text-black/60 mt-3 text-sm">
                  We&apos;ve routed your scenario to our recruiting desk. A PDF will arrive
                  shortly, followed by a personal email.
                </p>
                <button onClick={closeEmail} className="btn-secondary mt-8">
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function SliderRow({ label, suffix, value, onChange, display, min, max, step, testid, hint, gold, prefix }) {
  const clamp = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return value;
    return Math.min(Math.max(n, min), max);
  };
  const isPercent = String(display).includes("%");
  const derivedPrefix = prefix || (String(display).startsWith("$") ? "$" : "");
  const inputValue = value;
  const isInt = Number.isInteger(step);
  return (
    <div data-testid={testid}>
      <label className="field-label">{label}</label>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-baseline gap-1">
          {derivedPrefix && (
            <span className={`numeric text-3xl sm:text-4xl font-bold ${gold ? "text-[#121275]" : "text-[#0a0a0a]"}`}>
              {derivedPrefix}
            </span>
          )}
          <input
            type="number"
            value={isInt ? Math.round(inputValue) : inputValue}
            min={min}
            max={max}
            step={step}
            onChange={(e) => onChange(clamp(e.target.value))}
            className={`bg-transparent border-b border-black/25 focus:border-[#121275] outline-none numeric text-4xl sm:text-5xl font-bold tracking-tight w-40 sm:w-48 py-1 ${
              gold ? "text-[#121275]" : "text-[#0a0a0a]"
            }`}
            data-testid={`${testid}-input`}
          />
          {isPercent && (
            <span className={`numeric text-3xl sm:text-4xl font-bold ${gold ? "text-[#121275]" : "text-[#0a0a0a]"}`}>
              %
            </span>
          )}
        </div>
        <span className="numeric text-[11px] text-black/50 uppercase tracking-widest">
          {suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-4 accent-[#121275]"
        data-testid={`${testid}-range`}
      />
      {hint && (
        <div className="numeric text-[10px] text-black/35 tracking-widest uppercase mt-2">
          {hint}
        </div>
      )}
    </div>
  );
}
